import { useCallback } from 'react'

import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import {
  type SharePointChildrenResponse,
  sharepointChildrenQueryKeys,
} from '@/hooks/sharepoint/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { UUID } from '@/types/Id'

interface UseSharepointDriveChildrenArgs<T> {
  orgId: UUID | ''
  driveId: string
  itemId?: string // undefined for drive root
  enabled?: boolean
  limit?: number
  select?: (data: InfiniteData<SharePointChildrenResponse>) => T
}

export const fetchSharepointDriveChildren = async (
  orgId: UUID | '',
  driveId: string,
  accessToken: string,
  options: {
    itemId?: string
    limit?: number
    cursor?: string | null
  },
): Promise<SharePointChildrenResponse> => {
  if (!orgId) throw new Error('No org ID provided.')
  if (!driveId) throw new Error('No drive ID provided.')

  const params = new URLSearchParams()
  if (options.limit) params.set('limit', String(options.limit))
  if (options.cursor) params.set('cursor', options.cursor)

  // Use different endpoint depending on whether we're at root or in a folder
  const basePath = options.itemId
    ? `${API_URL}/api/v1/organizations/${orgId}/sharepoint/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(options.itemId)}/children`
    : `${API_URL}/api/v1/organizations/${orgId}/sharepoint/drives/${encodeURIComponent(driveId)}/root/children`

  const url = `${basePath}?${params.toString()}`

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok)
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  return (await res.json()) as SharePointChildrenResponse
}

export const useSharepointDriveChildren = <
  T = InfiniteData<SharePointChildrenResponse>,
>({
  orgId,
  driveId,
  itemId,
  enabled = true,
  limit = 50,
  select,
}: UseSharepointDriveChildrenArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: InfiniteData<SharePointChildrenResponse>): T => {
      return select ? select(data) : (data as T)
    },
    [select],
  )

  return useInfiniteQuery({
    queryKey: sharepointChildrenQueryKeys.folder({
      orgId,
      driveId,
      itemId,
    }),
    queryFn: ({ pageParam }) =>
      fetchSharepointDriveChildren(orgId, driveId, accessToken, {
        itemId,
        limit,
        cursor: pageParam ?? null,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_cursor : undefined,
    enabled: !!orgId && !!driveId && enabled,
    staleTime: 60 * 1000, // 1 minute
    select: selectFn,
  })
}
